// The user's review of a PR (SPEC §6 "Review flow (GraphQL)"):
// comments are batched into ONE pending review, then submitted as Comment, Approve or
// Request changes. Every mutation goes through GitHub.mutate, which throws on a null
// payload (GitHub rejects out-of-hunk lines silently with `thread: null`).
import { NullPayloadError, type GitHub } from '../github/client';
import type { Anchor } from './anchors';
import { fileCommentBody, lineCommentBody, neutralizeSuggestions } from './compose';

export type ReviewEvent = 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES';

export type NewComment = {
  path: string;
  anchor: Anchor;
  comment: string;
  /** Only allowed when the anchor covers the whole unit (UI enforces it). */
  suggestion?: string;
  /** Unit text, quoted in the body if the comment ends up file-level. */
  quote: string;
  /** Head lines of the unit (for the file-level marker). */
  unitStart: number;
  unitEnd: number;
};

export type AddResult = {
  threadId: string;
  /** Posted on the whole file: the unit was outside the diff, or GitHub refused the lines. */
  fileLevel: boolean;
  /** GitHub refused the line comment (null thread) and we fell back to file-level. */
  fellBack: boolean;
  /** A suggestion fence was turned into a plain code block. */
  suggestionNeutralized: boolean;
};

const ADD_REVIEW = `mutation($pr: ID!) { addPullRequestReview(input: { pullRequestId: $pr }) { pullRequestReview { id } } }`;
const ADD_THREAD = `mutation($input: AddPullRequestReviewThreadInput!) { addPullRequestReviewThread(input: $input) { thread { id } } }`;
const REPLY = `mutation($input: AddPullRequestReviewThreadReplyInput!) { addPullRequestReviewThreadReply(input: $input) { comment { id } } }`;
const SUBMIT = `mutation($id: ID!, $event: PullRequestReviewEvent!, $body: String) { submitPullRequestReview(input: { pullRequestReviewId: $id, event: $event, body: $body }) { pullRequestReview { id state } } }`;
const DELETE = `mutation($id: ID!) { deletePullRequestReview(input: { pullRequestReviewId: $id }) { pullRequestReview { id } } }`;

export class ReviewSession {
  #reviewId: string | null;
  readonly #gh: GitHub;
  readonly #prId: string;
  readonly #headSha: string;

  constructor(gh: GitHub, pr: { id: string; headSha: string; pendingReviewId: string | null }) {
    this.#gh = gh;
    this.#prId = pr.id;
    this.#headSha = pr.headSha;
    this.#reviewId = pr.pendingReviewId; // resume the viewer's pending review if there is one
  }

  get pendingReviewId(): string | null { return this.#reviewId; }

  async #ensureReview(): Promise<string> {
    if (this.#reviewId) return this.#reviewId;
    const d = await this.#gh.mutate<{ addPullRequestReview: { pullRequestReview: { id: string } } }>(
      ADD_REVIEW, { pr: this.#prId }, ['addPullRequestReview.pullRequestReview.id']);
    this.#reviewId = d.addPullRequestReview.pullRequestReview.id;
    return this.#reviewId;
  }

  async #addThread(input: Record<string, unknown>): Promise<string> {
    const d = await this.#gh.mutate<{ addPullRequestReviewThread: { thread: { id: string } } }>(
      ADD_THREAD, { input: { pullRequestReviewId: await this.#ensureReview(), ...input } }, ['addPullRequestReviewThread.thread.id']);
    return d.addPullRequestReviewThread.thread.id;
  }

  async #addFileThread(c: NewComment): Promise<{ id: string; neutralized: boolean }> {
    const neutralized = neutralizeSuggestions(c.comment).changed || c.suggestion !== undefined;
    const comment = c.suggestion !== undefined ? lineCommentBody(c.comment, c.suggestion) : c.comment;
    const body = fileCommentBody({ path: c.path, startLine: c.unitStart, endLine: c.unitEnd, headSha: this.#headSha, quote: c.quote, comment });
    return { id: await this.#addThread({ path: c.path, body, subjectType: 'FILE' }), neutralized };
  }

  /** Add a comment to the pending review: on lines when possible, else on the file. */
  async addComment(c: NewComment): Promise<AddResult> {
    if (c.anchor.kind === 'file') {
      const r = await this.#addFileThread(c);
      return { threadId: r.id, fileLevel: true, fellBack: false, suggestionNeutralized: r.neutralized };
    }
    const { startLine, line } = c.anchor;
    try {
      const id = await this.#addThread({
        path: c.path, body: lineCommentBody(c.comment, c.suggestion), subjectType: 'LINE', side: 'RIGHT', line,
        ...(startLine !== line ? { startLine, startSide: 'RIGHT' } : {}),
      });
      return { threadId: id, fileLevel: false, fellBack: false, suggestionNeutralized: false };
    } catch (e) {
      if (!(e instanceof NullPayloadError)) throw e;
      // GitHub refused these lines silently: keep the user's text, post it on the file.
      const r = await this.#addFileThread(c);
      return { threadId: r.id, fileLevel: true, fellBack: true, suggestionNeutralized: r.neutralized };
    }
  }

  /**
   * Reply in a thread. Attaches to the pending review when one exists; otherwise GitHub
   * posts it right away. Replies in FILE threads are file-level: suggestions neutralized.
   */
  async reply(threadId: string, body: string, fileThread: boolean): Promise<{ suggestionNeutralized: boolean }> {
    const n = fileThread ? neutralizeSuggestions(body) : { text: body, changed: false };
    await this.#gh.mutate(REPLY, {
      input: { pullRequestReviewThreadId: threadId, body: n.text, ...(this.#reviewId ? { pullRequestReviewId: this.#reviewId } : {}) },
    }, ['addPullRequestReviewThreadReply.comment.id']);
    return { suggestionNeutralized: n.changed };
  }

  async submit(event: ReviewEvent, body: string): Promise<void> {
    const id = await this.#ensureReview();
    await this.#gh.mutate(SUBMIT, { id, event, body: body.trim() || null }, ['submitPullRequestReview.pullRequestReview.id']);
    this.#reviewId = null;
  }

  async discard(): Promise<void> {
    if (!this.#reviewId) return;
    await this.#gh.mutate(DELETE, { id: this.#reviewId }, ['deletePullRequestReview.pullRequestReview.id']);
    this.#reviewId = null;
  }
}

/** Command-line equivalent shown next to Submit (SPEC §10: show the command behind each action). */
export function reviewCommand(pr: number, event: ReviewEvent): string {
  return `gh pr review ${pr} ${event === 'APPROVE' ? '--approve' : event === 'REQUEST_CHANGES' ? '--request-changes' : '--comment'}`;
}
